import { useEffect, useMemo, useState } from "react";
import { Plus, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { DatePicker } from "@/components/ui/date-picker";
import { LoadingAnimation } from "@/shared/components/LoadingAnimation";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Field, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TablePagination } from "@/shared/components/TablePagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { CustomerSearchSelect } from "@/shared/components/CustomerSearchSelect";
import { UserSearchSelect } from "@/shared/components/UserSearchSelect";
import { useDebounce } from "@/shared/hooks/useDebounce";
import {
  useCallDirections,
  useCallSources,
  useInquiryPriorities,
  useInquiryStatuses,
} from "@/shared/hooks/useMetaData";
import { useNotification } from "@/shared/hooks/useNotification";
import { getErrorMessage } from "@/shared/utils/errorHandler";
import { CallLogsTable } from "../components/CallLogsTable";
import { useAuthStore } from "@/features/auth/stores/authStore";
import { CustomerMessageDialog } from "@/features/callCenter/components/CustomerMessageDialog";
import {
  useCallLogs,
  useCreateCallLog,
  useDeleteCallLog,
  useUpdateCallLog,
} from "../hooks/useCallLogs";
import type {
  CallLog,
  CreateCallLogRequest,
  UpdateCallLogRequest,
} from "../services/callLogsApi";

interface CallLogFormState {
  callerName: string;
  phone: string;
  email: string;
  subject: string;
  inquiryDetails: string;
  status: string;
  priority: string;
  inquiryDate: string;
  direction: string;
  source: string;
  productServiceInterest: string;
  followUpDate: string;
  remarks: string;
  resolutionNotes: string;
  assignedToUserId: string;
  assignedToUserName: string;
  customerId: string;
  customerName: string;
}

const toIsoDate = (value?: string) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toISOString().slice(0, 10);
};

const toIsoOrUndefined = (value: string) => {
  if (!value) {
    return undefined;
  }

  return new Date(value).toISOString();
};

const initialFormState: CallLogFormState = {
  callerName: "",
  phone: "",
  email: "",
  subject: "",
  inquiryDetails: "",
  status: "0",
  priority: "0",
  inquiryDate: toIsoDate(new Date().toISOString()),
  direction: "0",
  source: "0",
  productServiceInterest: "",
  followUpDate: "",
  remarks: "",
  resolutionNotes: "",
  assignedToUserId: "",
  assignedToUserName: "",
  customerId: "",
  customerName: "",
};

const mapCallLogToFormState = (callLog: CallLog): CallLogFormState => ({
  callerName: callLog.callerName ?? "",
  phone: callLog.phone ?? "",
  email: callLog.email ?? "",
  subject: callLog.subject ?? "",
  inquiryDetails: callLog.inquiryDetails ?? "",
  status: String(callLog.status ?? 0),
  priority: String(callLog.priority ?? 0),
  inquiryDate: toIsoDate(callLog.inquiryDate),
  direction: String(callLog.direction ?? 0),
  source: String(callLog.source ?? 0),
  productServiceInterest: callLog.productServiceInterest ?? "",
  followUpDate: toIsoDate(callLog.followUpDate),
  remarks: callLog.remarks ?? "",
  resolutionNotes: callLog.resolutionNotes ?? "",
  assignedToUserId: callLog.assignedToUserId ?? "",
  assignedToUserName: callLog.assignedToUserName ?? "",
  customerId: callLog.customerId ?? "",
  customerName: callLog.customerName ?? "",
});

export const CallLogsManagement = () => {
  const notify = useNotification();
  const canPreviewMessages = useAuthStore((state) => state.hasPermission("call_center.messages.preview"));

  const [formState, setFormState] = useState<CallLogFormState>(initialFormState);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isFormDialogOpen, setIsFormDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [dueFollowUpsOnly, setDueFollowUpsOnly] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [messageCallLog, setMessageCallLog] = useState<CallLog>();

  const debouncedSearchTerm = useDebounce(searchTerm, 350);

  const { data: inquiryStatuses = [] } = useInquiryStatuses();
  const { data: inquiryPriorities = [] } = useInquiryPriorities();
  const { data: callDirections = [] } = useCallDirections();
  const { data: callSources = [] } = useCallSources();

  const filters = useMemo(
    () => ({
      pageNumber,
      pageSize,
      searchTerm: debouncedSearchTerm,
      dueFollowUpsOnly,
      status: dueFollowUpsOnly || debouncedSearchTerm.trim()
        ? undefined
        : statusFilter === "all"
          ? undefined
          : Number(statusFilter),
    }),
    [debouncedSearchTerm, dueFollowUpsOnly, statusFilter, pageNumber, pageSize]
  );

  const callLogsQuery = useCallLogs(filters);
  const createCallLogMutation = useCreateCallLog();
  const updateCallLogMutation = useUpdateCallLog();
  const deleteCallLogMutation = useDeleteCallLog();

  const isSubmitting = createCallLogMutation.isPending || updateCallLogMutation.isPending;

  useEffect(() => {
    const data = callLogsQuery.data;
    if (!data) {
      return;
    }

    const lastPage = Math.max(1, data.totalPages || Math.ceil(data.totalCount / data.pageSize) || 1);
    if (pageNumber > lastPage) {
      const timeoutId = window.setTimeout(() => setPageNumber(lastPage), 0);
      return () => window.clearTimeout(timeoutId);
    }
  }, [callLogsQuery.data, pageNumber]);

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setPageNumber(1);
  };

  const totalPages = Math.max(
    1,
    callLogsQuery.data?.totalPages ??
      (callLogsQuery.data
        ? Math.ceil(callLogsQuery.data.totalCount / callLogsQuery.data.pageSize)
        : 1)
  );

  const visiblePages = (() => {
    const pages: number[] = [];
    const start = Math.max(1, pageNumber - 2);
    const end = Math.min(totalPages, pageNumber + 2);
    for (let page = start; page <= end; page += 1) {
      pages.push(page);
    }
    return pages;
  })();

  const resetForm = () => {
    setFormState(initialFormState);
    setEditingId(null);
    setIsFormDialogOpen(false);
  };

  const validateForm = () => {
    if (!formState.callerName.trim()) {
      notify.error("Caller name is required");
      return false;
    }

    if (!formState.subject.trim()) {
      notify.error("Subject is required");
      return false;
    }

    if (!formState.inquiryDetails.trim()) {
      notify.error("Inquiry details are required");
      return false;
    }

    if (!formState.inquiryDate) {
      notify.error("Inquiry date is required");
      return false;
    }

    return true;
  };

  const createPayload = (): CreateCallLogRequest => ({
    callerName: formState.callerName.trim(),
    phone: formState.phone.trim() || undefined,
    email: formState.email.trim() || undefined,
    subject: formState.subject.trim(),
    inquiryDetails: formState.inquiryDetails.trim(),
    priority: Number(formState.priority),
    inquiryDate: new Date(formState.inquiryDate).toISOString(),
    direction: Number(formState.direction),
    source: Number(formState.source),
    productServiceInterest: formState.productServiceInterest.trim() || undefined,
    followUpDate: toIsoOrUndefined(formState.followUpDate),
    remarks: formState.remarks.trim() || undefined,
    resolutionNotes: formState.resolutionNotes.trim() || undefined,
    assignedToUserId: formState.assignedToUserId || undefined,
    customerId: formState.customerId || undefined,
  });

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    const payload = createPayload();

    if (editingId) {
      const updatePayload: UpdateCallLogRequest = {
        ...payload,
        status: Number(formState.status),
      };

      updateCallLogMutation.mutate(
        { id: editingId, payload: updatePayload },
        {
          onSuccess: () => {
            notify.success("Call log updated successfully");
            resetForm();
          },
          onError: (error) => {
            notify.error(getErrorMessage(error));
          },
        }
      );
      return;
    }

    createCallLogMutation.mutate(payload, {
      onSuccess: () => {
        notify.success("Call log created successfully");
        resetForm();
      },
      onError: (error) => {
        notify.error(getErrorMessage(error));
      },
    });
  };

  const handleEdit = (callLog: CallLog) => {
    setEditingId(callLog.id);
    setFormState(mapCallLogToFormState(callLog));
    setIsFormDialogOpen(true);
  };

  const handleCreateNew = () => {
    setEditingId(null);
    setFormState(initialFormState);
    setIsFormDialogOpen(true);
  };

  const handleDelete = (callLog: CallLog) => {
    deleteCallLogMutation.mutate(callLog.id, {
      onSuccess: () => {
        notify.success("Call log deleted successfully");
        if (editingId === callLog.id) {
          resetForm();
        }
      },
      onError: (error) => {
        notify.error(getErrorMessage(error));
      },
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Call Logs</h1>
          <p className="text-gray-500 mt-1">
            Capture customer inquiries and track follow-ups.
          </p>
        </div>

        <Dialog
          open={isFormDialogOpen}
          onOpenChange={(open) => {
            setIsFormDialogOpen(open);
            if (!open) {
              setEditingId(null);
              setFormState(initialFormState);
            }
          }}
        >
          <DialogTrigger asChild>
            <Button onClick={handleCreateNew} className="gap-2">
              <Plus className="h-4 w-4" />
              Record New Call Log
            </Button>
          </DialogTrigger>
          <DialogContent
            className="max-h-[90vh] overflow-y-auto"
            style={{ width: "80vw", maxWidth: "1000px" }}
          >
            <DialogHeader>
              <DialogTitle>{editingId ? "Edit Call Log" : "Record New Call Log"}</DialogTitle>
              <DialogDescription>
                {editingId
                  ? "Update inquiry details and follow-up information."
                  : "Capture a new customer inquiry and follow-up details."}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <Field>
                  <FieldLabel htmlFor="callerName">
                    Caller Name <span className="text-red-500">*</span>
                  </FieldLabel>
                  <Input
                    id="callerName"
                    value={formState.callerName}
                    onChange={(event) =>
                      setFormState((prev) => ({ ...prev, callerName: event.target.value }))
                    }
                    disabled={isSubmitting}
                  />
                </Field>

                <Field>
                  <FieldLabel htmlFor="subject">
                    Subject <span className="text-red-500">*</span>
                  </FieldLabel>
                  <Input
                    id="subject"
                    value={formState.subject}
                    onChange={(event) =>
                      setFormState((prev) => ({ ...prev, subject: event.target.value }))
                    }
                    disabled={isSubmitting}
                  />
                </Field>

                <Field>
                  <FieldLabel htmlFor="phone">Phone</FieldLabel>
                  <Input
                    id="phone"
                    value={formState.phone}
                    onChange={(event) =>
                      setFormState((prev) => ({ ...prev, phone: event.target.value }))
                    }
                    disabled={isSubmitting}
                  />
                </Field>

                <Field>
                  <FieldLabel htmlFor="email">Email</FieldLabel>
                  <Input
                    id="email"
                    type="email"
                    value={formState.email}
                    onChange={(event) =>
                      setFormState((prev) => ({ ...prev, email: event.target.value }))
                    }
                    disabled={isSubmitting}
                  />
                </Field>

                <Field>
                  <FieldLabel htmlFor="priority">Priority</FieldLabel>
                  <Select
                    value={formState.priority}
                    onValueChange={(value) =>
                      setFormState((prev) => ({ ...prev, priority: value }))
                    }
                    disabled={isSubmitting}
                  >
                    <SelectTrigger id="priority">
                      <SelectValue placeholder="Select priority" />
                    </SelectTrigger>
                    <SelectContent>
                      {inquiryPriorities.map((option) => (
                        <SelectItem key={option.value} value={String(option.value)}>
                          {option.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>

                <Field>
                  <FieldLabel htmlFor="direction">Direction</FieldLabel>
                  <Select
                    value={formState.direction}
                    onValueChange={(value) =>
                      setFormState((prev) => ({ ...prev, direction: value }))
                    }
                    disabled={isSubmitting}
                  >
                    <SelectTrigger id="direction">
                      <SelectValue placeholder="Select direction" />
                    </SelectTrigger>
                    <SelectContent>
                      {callDirections.map((option) => (
                        <SelectItem key={option.value} value={String(option.value)}>
                          {option.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>

                <Field>
                  <FieldLabel htmlFor="source">Source</FieldLabel>
                  <Select
                    value={formState.source}
                    onValueChange={(value) =>
                      setFormState((prev) => ({ ...prev, source: value }))
                    }
                    disabled={isSubmitting}
                  >
                    <SelectTrigger id="source">
                      <SelectValue placeholder="Select source" />
                    </SelectTrigger>
                    <SelectContent>
                      {callSources.map((option) => (
                        <SelectItem key={option.value} value={String(option.value)}>
                          {option.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>

                <Field>
                  <FieldLabel htmlFor="inquiryDate">Inquiry Date</FieldLabel>
                  <DatePicker
                    id="inquiryDate"
                    value={formState.inquiryDate}
                    onChange={(value) =>
                      setFormState((prev) => ({ ...prev, inquiryDate: value }))
                    }
                    disabled={isSubmitting}
                  />
                </Field>

                <Field>
                  <FieldLabel htmlFor="followUpDate">Follow-up Date</FieldLabel>
                  <DatePicker
                    id="followUpDate"
                    value={formState.followUpDate}
                    onChange={(value) =>
                      setFormState((prev) => ({ ...prev, followUpDate: value }))
                    }
                    disabled={isSubmitting}
                  />
                </Field>

                {editingId && (
                  <Field>
                    <FieldLabel htmlFor="status">Status</FieldLabel>
                    <Select
                      value={formState.status}
                      onValueChange={(value) =>
                        setFormState((prev) => ({ ...prev, status: value }))
                      }
                      disabled={isSubmitting}
                    >
                      <SelectTrigger id="status">
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        {inquiryStatuses.map((option) => (
                          <SelectItem key={option.value} value={String(option.value)}>
                            {option.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                )}

                <Field>
                  <FieldLabel htmlFor="interest">Product/Service Interest</FieldLabel>
                  <Input
                    id="interest"
                    value={formState.productServiceInterest}
                    onChange={(event) =>
                      setFormState((prev) => ({ ...prev, productServiceInterest: event.target.value }))
                    }
                    disabled={isSubmitting}
                  />
                </Field>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <CustomerSearchSelect
                  selectedCustomerId={formState.customerId}
                  selectedCustomerName={formState.customerName}
                  onSelect={(customerId, customerName) =>
                    setFormState((prev) => ({
                      ...prev,
                      customerId,
                      customerName,
                    }))
                  }
                  disabled={isSubmitting}
                />

                <UserSearchSelect
                  selectedUserId={formState.assignedToUserId}
                  selectedUserName={formState.assignedToUserName}
                  onSelect={(user) =>
                    setFormState((prev) => ({
                      ...prev,
                      assignedToUserId: user?.id ?? "",
                      assignedToUserName: user
                        ? `${user.firstName} ${user.otherNames}`
                        : "",
                    }))
                  }
                  label="Assign To"
                  disabled={isSubmitting}
                />
              </div>

              <Field>
                <FieldLabel htmlFor="inquiryDetails">
                  Inquiry Details <span className="text-red-500">*</span>
                </FieldLabel>
                <Textarea
                  id="inquiryDetails"
                  rows={4}
                  value={formState.inquiryDetails}
                  onChange={(event) =>
                    setFormState((prev) => ({ ...prev, inquiryDetails: event.target.value }))
                  }
                  disabled={isSubmitting}
                />
              </Field>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <Field>
                  <FieldLabel htmlFor="remarks">Remarks</FieldLabel>
                  <Textarea
                    id="remarks"
                    rows={3}
                    value={formState.remarks}
                    onChange={(event) =>
                      setFormState((prev) => ({ ...prev, remarks: event.target.value }))
                    }
                    disabled={isSubmitting}
                  />
                </Field>

                <Field>
                  <FieldLabel htmlFor="resolutionNotes">Resolution Notes</FieldLabel>
                  <Textarea
                    id="resolutionNotes"
                    rows={3}
                    value={formState.resolutionNotes}
                    onChange={(event) =>
                      setFormState((prev) => ({ ...prev, resolutionNotes: event.target.value }))
                    }
                    disabled={isSubmitting}
                  />
                </Field>
              </div>

              <div className="flex items-center gap-2">
                <Button type="button" onClick={handleSubmit} disabled={isSubmitting}>
                  {editingId
                    ? updateCallLogMutation.isPending
                      ? "Updating..."
                      : "Update Call Log"
                    : createCallLogMutation.isPending
                      ? "Saving..."
                      : "Save Call Log"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={resetForm}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Inquiries</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <Field>
              <FieldLabel htmlFor="search">Search</FieldLabel>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="search"
                  placeholder="Search by caller, subject, phone..."
                  className="pl-10"
                  value={searchTerm}
                  onChange={(event) => { setSearchTerm(event.target.value); setPageNumber(1); }}
                />
              </div>
            </Field>

            <Field>
              <FieldLabel htmlFor="status-filter">Status Filter</FieldLabel>
              <Select
                value={statusFilter}
                onValueChange={(value) => { setStatusFilter(value); setPageNumber(1); }}
                disabled={dueFollowUpsOnly || !!debouncedSearchTerm.trim()}
              >
                <SelectTrigger id="status-filter">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {inquiryStatuses.map((option) => (
                    <SelectItem key={option.value} value={String(option.value)}>
                      {option.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <div className="flex items-end">
              <div className="flex items-center space-x-2 pb-2">
                <Checkbox
                  id="due-followups"
                  checked={dueFollowUpsOnly}
                  onCheckedChange={(checked) => { const enabled = Boolean(checked); setDueFollowUpsOnly(enabled); if (enabled) setStatusFilter("all"); setPageNumber(1); }}
                />
                <Label htmlFor="due-followups">Due follow-ups only</Label>
              </div>
            </div>
          </div>

          {callLogsQuery.isLoading ? (
            <LoadingAnimation text="Loading call logs..." />
          ) : callLogsQuery.isError ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
              Failed to load call logs.
            </div>
          ) : (
            <CallLogsTable
              callLogs={callLogsQuery.data?.items ?? []}
              deletingId={deleteCallLogMutation.variables}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onSendMessage={canPreviewMessages ? setMessageCallLog : undefined}
            />
          )}

          {!callLogsQuery.isLoading && !callLogsQuery.isError && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-center gap-2">
                {visiblePages.map((page) => (
                  <Button
                    key={page}
                    size="sm"
                    variant={page === pageNumber ? "default" : "outline"}
                    onClick={() => setPageNumber(page)}
                    disabled={callLogsQuery.isFetching}
                  >
                    {page}
                  </Button>
                ))}
              </div>

              <TablePagination
                pageNumber={callLogsQuery.data?.pageNumber ?? pageNumber}
                pageSize={callLogsQuery.data?.pageSize ?? pageSize}
                totalCount={callLogsQuery.data?.totalCount ?? 0}
                totalPages={callLogsQuery.data?.totalPages ?? totalPages}
                onPageChange={setPageNumber}
                onPageSizeChange={handlePageSizeChange}
                pageSizeOptions={[10, 20, 50, 100]}
                isFetching={callLogsQuery.isFetching}
              />
            </div>
          )}
        </CardContent>
      </Card>
      <CustomerMessageDialog key={messageCallLog?.id ?? "no-call"} open={Boolean(messageCallLog)} onOpenChange={(open) => { if (!open) setMessageCallLog(undefined); }} customerId={messageCallLog?.customerId} customerName={messageCallLog?.customerName} callLogId={messageCallLog?.id} onQueued={() => notify.success("Message queued for sending")} />
    </div>
  );
};
